import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconArrowRight } from '@tabler/icons-react'
import { isEmpty, isNil } from 'lodash'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Card } from '@/components/cards/Card'
import { fetchUserAudit } from '@/reducers/organizations/organizationUsers.actions'
import { setUserAudit } from '@/reducers/organizations/organizationUsers.redux'
import { Pill } from '@/components/pills/Pill'
import { cleanBooleanValue, formatTime, truncatedTooltip } from '@/utils/helpers'
import { PERMISSION_LABELS } from '../organization.schema'

import '../organizations.scss'

const mapColumNames = {
  ...PERMISSION_LABELS,
  code: 'VoIP User ID',
  config_cid: 'Playbook',
  deleted: 'Agent Status',
  first_name: 'First Name',
  last_name: 'Last Name',
  password: 'Balto Cloud Access',
  screen_capture_enabled: 'Screen Capture',
  uncategorizedTags: 'Uncategorized Tags',
  username: 'Email',
}

// Redundant with the table's "Who changed it" / "When it happened" columns
const HIDDEN_COLUMNS = ['last_modified_by', 'last_modified_on']

const isBool = (value) => !isNil(cleanBooleanValue(value))

const onOffPill = (value) => {
  const on = Boolean(cleanBooleanValue(value))

  return (
    <Pill small success={on} critical={!on}>
      {on ? 'On' : 'Off'}
    </Pill>
  )
}

const PLAYBOOK_NAME_MAX_LENGTH = 35

const playbookName = (playbooks, cid) =>
  truncatedTooltip(
    playbooks.find((playbook) => playbook.cid === cid)?.name,
    PLAYBOOK_NAME_MAX_LENGTH
  )

const getColumnName = (column) =>
  mapColumNames[column] ??
  column
    .split(/[\s_]+/)
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ')

const changeArrow = (oldContent, newContent) => (
  <div className="audit-change">
    <span className="flex-align-center small-gap">{oldContent}</span>
    <IconArrowRight size={14} />
    <span className="flex-align-center small-gap">{newContent}</span>
  </div>
)

const tagPills = (tags) => {
  if (isEmpty(tags)) {
    return 'None'
  }

  return tags.map((item) => (
    <Pill small key={item.value}>
      {item.label ?? 'None'}
    </Pill>
  ))
}

const buildChange = (key, change, playbooks) => {
  let type = change?.type
  if (isBool(change?.new) || isBool(change?.old)) {
    type = 'boolean'
  } else if (key === 'config_cid' && change?.type === 'value') {
    type = 'playbook'
  }

  switch (type) {
    case 'new_user':
      return {
        what: 'User',
        change: `Added ${key === 'new_user_single' ? '(manually)' : '(bulk)'}`,
      }
    case 'user_update':
      if (key === 'bulk_upload') {
        return { what: 'User', change: 'Bulk updated' }
      }

      return null
    case 'boolean':
      if (key === 'deleted') {
        return {
          what: getColumnName(key),
          change: changeArrow(
            cleanBooleanValue(change?.old) ? 'Deactivated' : 'Active',
            cleanBooleanValue(change?.new) ? 'Deactivated' : 'Active'
          ),
        }
      }

      return {
        what: getColumnName(key),
        change: changeArrow(onOffPill(change?.old), onOffPill(change?.new)),
      }
    case 'categorizedTags':
      return {
        what: getColumnName(key),
        change: changeArrow(
          change?.old?.value ? <Pill small>{change.old.label}</Pill> : 'None',
          change?.new?.value ? <Pill small>{change.new.label}</Pill> : 'None'
        ),
      }
    case 'uncategorizedTags':
      return {
        what: getColumnName(key),
        change: changeArrow(tagPills(change?.old), tagPills(change?.new)),
      }
    case 'playbook':
      return {
        what: getColumnName(key),
        change: changeArrow(
          playbookName(playbooks, change?.old),
          playbookName(playbooks, change?.new)
        ),
      }
    case 'value':
      return {
        what: getColumnName(key),
        change: changeArrow(change?.old ?? 'None', change?.new ?? 'None'),
      }
    case 'reset_password_email':
      return { what: 'Password', change: 'Password Reset Email Sent' }
    case 'reset_password':
      return { what: 'Password', change: 'Add/Reset Password' }
    default:
      return null
  }
}

export const buildAuditRows = (userAudit, playbooks) => {
  const rows = []

  userAudit.forEach((event) => {
    Object.entries(event.event_payload ?? {}).forEach(([key, change]) => {
      if (HIDDEN_COLUMNS.includes(key)) {
        return
      }

      const changeRow = buildChange(key, change, playbooks)
      if (changeRow) {
        rows.push({
          id: `${event.id}-${key}`,
          ...changeRow,
          author: event.author,
          created_at: new Date(event.created_at).getTime(),
        })
      }
    })
  })

  return rows
}

const columns = [
  {
    accessor: 'what',
    label: 'What changed',
    isSearchable: true,
  },
  {
    accessor: 'change',
    label: 'Change',
    isSortable: false,
  },
  {
    accessor: 'author',
    label: 'Who changed it',
    isSearchable: true,
  },
  {
    accessor: 'created_at',
    label: 'When it happened',
    format: (createdAt) => formatTime(createdAt),
  },
]

export const UserAuditTable = ({ user }) => {
  const dispatch = useDispatch()
  const { loading, userAudit } = useSelector((state) => state.organizationUsers)
  const playbooks = useSelector((state) => state.playbooks.playbooks) ?? []

  useEffect(() => {
    dispatch(fetchUserAudit(user.id))

    return () => {
      dispatch(setUserAudit([]))
    }
  }, [user.id])

  return (
    <Card className="no-padding" data-testid="user-audit-table">
      <AdvancedTable
        celled
        columns={columns}
        loading={loading?.userAudit}
        noDataText="User has no Change History data"
        rows={buildAuditRows(userAudit, playbooks)}
        defaultOrderBy="created_at"
        defaultOrder="desc"
      />
    </Card>
  )
}
